import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  FileCode,
  RefreshCw,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Send,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import {
  platformApi,
  type MessageTemplate,
} from "@/services/api/platform";

export const Route = createFileRoute("/portal/messaging/templates")({
  component: TemplatesPage,
});

function getStatusBadge(status: string) {
  switch (status) {
    case "approved":
      return (
        <Badge variant="success">
          <CheckCircle className="h-3 w-3 mr-1" /> Approved
        </Badge>
      );
    case "pending_approval":
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
          <Clock className="h-3 w-3 mr-1" /> Pending
        </Badge>
      );
    case "rejected":
      return (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" /> Rejected
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary">
          <AlertCircle className="h-3 w-3 mr-1" /> Draft
        </Badge>
      );
  }
}

function TemplatesPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId;
  const { can } = usePermissions();
  // ME6: sync-from-Meta and submit-to-Meta are channel/template config actions
  // (real external side effects) — org_admin+ only. Members keep read access.
  const canManageTemplates = can("edit", "messaging");

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [confirmSubmit, setConfirmSubmit] = useState<MessageTemplate | null>(null);

  const fetchTemplates = useCallback(async () => {
    if (!token || !organisationId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await platformApi.listTemplates(token, organisationId);
      setTemplates(data);
    } catch (err) {
      // ME28: surface the failure instead of rendering an empty list. A
      // swallowed error looked like "no templates" and could tempt a re-sync /
      // re-create that regenerates the Meta handshake state.
      setError(err instanceof Error ? err.message : "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [token, organisationId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSync = async () => {
    if (!token || !organisationId) return;
    setSyncing(true);
    try {
      const result = await platformApi.syncWhatsAppTemplates(token, organisationId);
      if (result.errors.length > 0) {
        toast.warning(`Synced ${result.synced} templates with ${result.errors.length} errors`);
      } else {
        toast.success(`Synced ${result.synced} templates (${result.created} new, ${result.updated} updated)`);
      }
      fetchTemplates();
    } catch {
      toast.error("Failed to sync templates from Meta");
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async (template: MessageTemplate) => {
    if (!token) return;
    setSubmittingId(template.id);
    setConfirmSubmit(null);
    try {
      await platformApi.submitWhatsAppTemplate(token, template.id);
      toast.success(`Template "${template.name}" submitted to Meta for approval`);
      fetchTemplates();
    } catch {
      toast.error(`Failed to submit template "${template.name}"`);
    } finally {
      setSubmittingId(null);
    }
  };

  const approvedCount = templates.filter((t) =>
    t.versions.some((v) => v.status === "approved"),
  ).length;
  const pendingCount = templates.filter((t) =>
    t.versions.some((v) => v.status === "pending_approval"),
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Templates</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage WhatsApp message templates and Meta approval status
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTemplates} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
          {canManageTemplates && (
            <Button onClick={handleSync} disabled={syncing}>
              {syncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Sync from Meta
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Templates</CardTitle>
            <FileCode className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{templates.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{approvedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Approval</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Templates Table */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-12" role="alert">
            <FileCode className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h3 className="text-lg font-medium mb-2">Couldn&apos;t load templates</h3>
            <p className="text-muted-foreground mb-4">
              {error}. This does not mean your templates are gone — do not
              re-sync or re-create them until this loads.
            </p>
            <Button variant="outline" onClick={fetchTemplates}>
              Retry
            </Button>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <FileCode className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No templates yet</h3>
            <p className="text-muted-foreground mb-4">
              {canManageTemplates
                ? 'Click "Sync from Meta" to import your WhatsApp Business templates, or create templates in the Meta Business Manager first.'
                : "No WhatsApp templates have been imported yet. Ask an organisation admin to sync them from Meta."}
            </p>
            {canManageTemplates && (
              <Button onClick={handleSync} disabled={syncing}>
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Sync from Meta
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Meta ID</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => {
                const waVersion = template.versions.find((v) => v.channel === "whatsapp");
                const status = waVersion?.status || "draft";
                const canSubmit = status === "draft" || status === "rejected";

                return (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{template.name}</div>
                        {template.description && (
                          <div className="text-sm text-muted-foreground">
                            {template.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">
                        {template.category || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {template.versions.map((v) => (
                        <Badge key={v.id} variant="outline" className="text-xs capitalize mr-1">
                          {v.channel}
                        </Badge>
                      ))}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(status)}
                      {waVersion?.rejection_reason && (
                        <p className="text-xs text-destructive mt-1">
                          {waVersion.rejection_reason}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground font-mono">
                        {waVersion?.external_id || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {canSubmit && canManageTemplates && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmSubmit(template)}
                          disabled={submittingId === template.id}
                        >
                          {submittingId === template.id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3 mr-1" />
                          )}
                          Submit
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Submit Confirmation */}
      <AlertDialog
        open={!!confirmSubmit}
        onOpenChange={(open) => !open && setConfirmSubmit(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Template to Meta?</AlertDialogTitle>
            <AlertDialogDescription>
              This will submit &quot;{confirmSubmit?.name}&quot; to Meta for WhatsApp approval.
              Approval typically takes 24-48 hours. Once approved, the template can be used
              to initiate conversations with customers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmSubmit && handleSubmit(confirmSubmit)}>
              Submit for Approval
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
